import { FormikBag, FormikProps, withFormik } from 'formik';
import { History } from 'history';
import React from 'react';
import * as yup from 'yup';
import { getGroups } from '../AppShell/Authentication/AuthorisationAsync';
import { GroupIGroup, UserIParentGroup, UserIUser } from '../AppShell/Authentication/AuthorisationModels';
import { invalidEmailMessage, requiredField } from '../Authorisation.en';
import { getAuditLine } from '../Components/AuditLine';
import { ButtonMode } from '../Components/Button';
import { StyledDialogActions } from '../Components/Dialog/StyledDialogActions';
import { StyledDialogButton } from '../Components/Dialog/StyledDialogButton';
import { StyledDialogContent } from '../Components/Dialog/StyledDialogContent';
import { FormikCheckbox } from '../Components/FormikFields/FormikCheckbox';
import { FormikSwitch } from '../Components/FormikFields/FormikSwitch';
import { FormikTextField } from '../Components/FormikFields/FormikTextField';
import { FormGrid } from '../Components/Layout/FormGrid';
import { AutoCompleteTypes } from '../Components/MaskedTextField';
import { StyledNavigationPrompt } from '../Components/Prompts/StyledNavigationPrompt';
import { StyledPrompt } from '../Components/Prompts/StyledPrompt';
import { StyledSelect } from '../Components/StyledSelect';
import { SectionTitle } from '../Components/Typography/SectionTitle';
import { defaultAuthoringSystem, selfAuthoringSystemForeignKey } from '../Shared/Constants';
import { CancelledRequest, HttpExecutorConfig } from '../Shared/HttpRequestExecutor';
import { deepGetFirstNumberOrString } from '../Shared/ObjectUtilities';
import { lookupIdentifierToKey, lookupIdentifierToName } from '../Shared/SharedModels';
import { standardName } from '../Shared/StringUtilities';
import { Nullable } from '../Shared/Types';
import { divider } from '../Styles/Colours';
import styles from './UserFileInformation.module.scss';

type UserFileInformationProps = {
    isNew?: boolean;
    isDeleting: boolean;
    user?: UserIUser;
    systemContact?: UserIUser;
    groups: UserIParentGroup[];
    defaultGroup?: GroupIGroup;
    history: History;
    loadInformation(): Promise<any>;
    onError(error: string): void;
    onClose(): void;
    onSaveUserInformation(user: UserIUser): Promise<string>;
};

type UserFileInformationFormProps = UserFileInformationProps & FormikProps<UserIUser>;

class ConfirmDefaultUserActions extends React.Component<{ cancel: () => void; save: () => void }> {
    public render() {
        return (
            <>
                <StyledDialogButton
                    borderColour={divider}
                    mode={ButtonMode.border}
                    testId='cancel'
                    key='default-close'
                    onClick={this.props.cancel}
                >
                    Cancel
                </StyledDialogButton>
                <StyledDialogButton testId='proceed' key='default-save' onClick={this.props.save}>
                    Proceed
                </StyledDialogButton>
            </>
        );
    }
}

const handleSubmit = async (values: UserIUser, formikBag: FormikBag<UserFileInformationProps, UserIUser>) => {
    const userId = await formikBag.props.onSaveUserInformation(values);
    formikBag.setSubmitting(false);

    if (userId) {
        formikBag.resetForm(values);
        if (formikBag.props.isNew) {
            formikBag.props.history.replace(`/settings/users/${userId}`);
        }
        formikBag.props.onClose();
    }
};

const mapPropsToValues = (props: UserFileInformationProps): UserIUser => {
    if (!!props && props.user) {
        return {
            id: props.user.id,
            givenNames: props.user.givenNames || '',
            familyName: props.user.familyName || '',
            username: props.user.username || '',
            email: props.user.email || '',
            enabled: props.user.enabled,
            isSystemContact: props.user.isSystemContact,
            authoringSystem: props.user.authoringSystem || '',
            authoringSystemForeignKey: props.user.authoringSystemForeignKey || '',
            groups: props.groups || [],
            createdBy: props.user.createdBy,
            createdOn: props.user.createdOn,
            updatedBy: props.user.updatedBy,
            updatedOn: props.user.updatedOn
        };
    }

    return {
        id: '',
        givenNames: '',
        familyName: '',
        username: '',
        email: '',
        enabled: true,
        isSystemContact: false,
        authoringSystem: defaultAuthoringSystem,
        authoringSystemForeignKey: selfAuthoringSystemForeignKey,
        groups: props.defaultGroup ? [props.defaultGroup] : [],
        createdBy: '',
        createdOn: '',
        updatedBy: '',
        updatedOn: ''
    };
};

const validationSchema = yup.object().shape({
    givenNames: yup.string().required(requiredField),
    familyName: yup.string().required(requiredField),
    username: yup.string().required(requiredField),
    email: yup.string().email(invalidEmailMessage).required(requiredField),
    enabled: yup.boolean().required(requiredField),
    isSystemContact: yup.boolean(),
    authoringSystem: yup.string().required(requiredField),
    authoringSystemForeignKey: yup.string().required(requiredField)
});

class UserFileInformationForm extends React.Component<UserFileInformationFormProps, { confirm: boolean }> {
    public state = {
        confirm: false
    };

    public componentWillUnmount() {
        this.props.onError('');
    }

    public submitAndCheckForSystemContact = async () => {
        const confirmAsSystemContact =
            (this.props.values.isSystemContact && this.props.systemContact && this.props.isNew) ||
            (this.props.values.isSystemContact &&
                this.props.systemContact &&
                this.props.user &&
                this.props.systemContact.id !== this.props.user.id) ||
            false;

        this.props.setSubmitting(true);

        if (confirmAsSystemContact) {
            this.setState({ confirm: confirmAsSystemContact });
        } else {
            this.validateAndSubmit();
        }
    };

    public submit = () => {
        this.setState(
            {
                confirm: false
            },
            () => {
                this.validateAndSubmit();
            }
        );
    };

    public onCancelSystemContact = () => {
        this.setState({ confirm: false });
        this.props.setSubmitting(false);
    };

    public validateAndSubmit = () => {
        this.props.validateForm();
        const flatErrors = deepGetFirstNumberOrString(this.props.errors);
        this.props.onError(flatErrors.toString());
        this.props.submitForm();
    };

    public searchForGroups = async (
        config: HttpExecutorConfig,
        value: string,
        limit: number
    ): Promise<GroupIGroup[] | CancelledRequest> => {
        return await getGroups(config, value, true, limit);
    };

    public onGroupSelection = (groups: Nullable<GroupIGroup[]>) => {
        this.props.setFieldTouched('groups');
        this.props.setFieldValue('groups', groups ?? []);
    };

    public render() {
        const props = this.props;

        const actions = (
            <>
                <StyledDialogButton
                    borderColour={divider}
                    mode={ButtonMode.border}
                    testId='cancel'
                    onClick={props.onClose}
                >
                    Cancel
                </StyledDialogButton>
                <StyledDialogButton
                    testId='save'
                    disabled={props.isSubmitting}
                    onClick={this.submitAndCheckForSystemContact}
                >
                    Save
                </StyledDialogButton>
            </>
        );

        return (
            <>
                <StyledDialogContent noPadding>
                    <div className={styles.container}>
                        <FormGrid row>
                            <FormGrid sectionTitle>
                                <SectionTitle h1 title='Account information' />
                            </FormGrid>
                        </FormGrid>
                        <FormGrid row>
                            <div className={styles.auditLineText}>
                                {!props.isNew &&
                                    getAuditLine({
                                        auditCreatedOn: props.values.createdOn,
                                        auditLastUpdated: props.values.updatedOn,
                                        auditLastUpdatedByUser: props.values.updatedBy
                                    })}
                            </div>
                        </FormGrid>
                        {!props.isNew && (
                            <FormGrid row marginBottom={10}>
                                <FormGrid input>
                                    <FormikTextField
                                        readOnly
                                        label='System name'
                                        name='authoringSystem'
                                        testId='system-name'
                                        noWrapReadOnly
                                    />
                                </FormGrid>
                                <FormGrid input>
                                    <FormikTextField
                                        readOnly
                                        label='System key'
                                        name='authoringSystemForeignKey'
                                        testId='system-key'
                                        noWrapReadOnly
                                    />
                                </FormGrid>
                            </FormGrid>
                        )}
                        <FormGrid row marginBottom={10}>
                            <FormGrid input>
                                <FormikTextField
                                    required
                                    readOnly={!props.isNew}
                                    label='User name'
                                    name='username'
                                    testId='test-id'
                                    noWrapReadOnly
                                />
                            </FormGrid>
                            <FormGrid input>
                                <FormikTextField
                                    required
                                    label='Email'
                                    name='email'
                                    autoCompleteType={AutoCompleteTypes.UserInfo}
                                    testId='email'
                                />
                            </FormGrid>
                        </FormGrid>
                        <FormGrid row marginBottom={10}>
                            <FormGrid input>
                                <FormikTextField
                                    required
                                    label='Family name'
                                    name='familyName'
                                    autoCompleteType={AutoCompleteTypes.UserInfo}
                                    testId='family'
                                />
                            </FormGrid>
                            <FormGrid input>
                                <FormikTextField
                                    required
                                    name='givenNames'
                                    label='Given names'
                                    autoCompleteType={AutoCompleteTypes.UserInfo}
                                    testId='given'
                                />
                            </FormGrid>
                        </FormGrid>
                        <FormGrid row>
                            <FormGrid input>
                                <div className={styles.userEnabled}>
                                    <div className={styles.userEnabledText}>User enabled</div>
                                    <FormikSwitch name='enabled' label='' />
                                </div>
                            </FormGrid>
                            <FormGrid input>
                                <FormikCheckbox name='isSystemContact' label='Set as system contact' />
                            </FormGrid>
                        </FormGrid>
                        <FormGrid row>
                            <div className={styles.lineSeperator} />
                        </FormGrid>
                        <FormGrid row>
                            <FormGrid sectionTitle>
                                <SectionTitle h1 title='Inherits permissions from' />
                            </FormGrid>
                        </FormGrid>
                        <FormGrid row>
                            <div className={styles.userGroups}>
                                <StyledSelect<UserIParentGroup>
                                    label='Add user to groups'
                                    testId='add-group'
                                    maxResults={5}
                                    value={this.props.values.groups}
                                    fetch={this.searchForGroups}
                                    itemToString={lookupIdentifierToName}
                                    itemToMenuKey={lookupIdentifierToKey}
                                    isMulti
                                    isClearable
                                    onSelection={this.onGroupSelection}
                                />
                            </div>
                        </FormGrid>
                        <StyledNavigationPrompt
                            when={!props.isDeleting && props.dirty}
                            onSave={this.submitAndCheckForSystemContact}
                        />
                        <StyledPrompt
                            actions={
                                <ConfirmDefaultUserActions save={this.submit} cancel={this.onCancelSystemContact} />
                            }
                            open={this.state.confirm}
                        >
                            {`You have already set ${
                                props.systemContact
                                    ? standardName({
                                          givenNames: props.systemContact.givenNames,
                                          familyName: props.systemContact.familyName
                                      })
                                    : ''
                            } as the system contact. If you proceed, they will no longer be set as the contact.`}
                        </StyledPrompt>
                    </div>
                </StyledDialogContent>
                <StyledDialogActions>{actions}</StyledDialogActions>
            </>
        );
    }
}

export const UserFileInformation = withFormik<UserFileInformationProps, UserIUser>({
    handleSubmit,
    mapPropsToValues,
    validationSchema
})(UserFileInformationForm);
